/**
 * Data Processing Hook
 * Handles data transformation and value key resolution
 */

import { useMemo, useCallback } from 'react';
import { DataPoint, ProcessedChartData } from '../types';

/**
 * Find the first numeric key in a data point
 */
export function useFindFirstNumericKey() {
  return useCallback((row: DataPoint): string | undefined => {
    const keys = Object.keys(row).filter((k) => k !== "UTC");
    for (const k of keys) {
      const val = row[k];
      if (typeof val === "string" && val.toLowerCase() === "nan") continue;
      if (typeof val === "number" && Number.isFinite(val)) return k;
      if (typeof val === "string" && !Number.isNaN(Number(val)) && Number.isFinite(Number(val)))
        return k;
    }
    return undefined;
  }, []);
}

/**
 * Resolve the value key from data
 */
export function useResolvedValueKey(
  data: DataPoint[],
  valueKey: string | undefined,
  findFirstNumericKey: (row: DataPoint) => string | undefined
): string | undefined {
  return useMemo(() => {
    if (valueKey && data.length > 0) return valueKey;
    if (data.length > 0) {
      const sampleSize = 100;
      const dataLength = data.length;
      if (dataLength <= sampleSize) {
        for (let i = 0; i < dataLength; i++) {
          const key = findFirstNumericKey(data[i]);
          if (key) return key;
        }
      } else {
        const step = Math.floor(dataLength / sampleSize);
        for (let i = 0; i < dataLength; i += step) {
          const key = findFirstNumericKey(data[i]);
          if (key) return key;
        }
        const lastKey = findFirstNumericKey(data[dataLength - 1]);
        if (lastKey) return lastKey;
      }
    }
    return undefined;
  }, [data, valueKey, findFirstNumericKey]);
}

/**
 * Process data into timestamps and values arrays
 */
export function useProcessedData(
  data: DataPoint[],
  resolvedValueKey: string | undefined
): ProcessedChartData {
  return useMemo(() => {
    if (!data || data.length === 0 || !resolvedValueKey) {
      return { timestamps: [], values: [], validCount: 0, yMin: 0, yMax: 0 };
    }

    const timestamps: number[] = [];
    const values: (number | null)[] = [];
    let validCount = 0;
    let yMin = Infinity;
    let yMax = -Infinity;

    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      const ts = new Date(d.UTC).getTime() / 1000; // uPlot uses seconds
      if (!Number.isFinite(ts)) continue;

      timestamps.push(ts);

      const raw = d[resolvedValueKey];
      let v: number | null = null;
      if (raw === null || raw === undefined || raw === "None" || raw === "NaN") {
        v = null;
      } else if (typeof raw === "number") {
        v = Number.isFinite(raw) ? raw : null;
      } else if (typeof raw === "string") {
        const parsed = Number(raw);
        v = Number.isFinite(parsed) ? parsed : null;
      }

      values.push(v);

      if (v !== null) {
        validCount++;
        if (v < yMin) yMin = v;
        if (v > yMax) yMax = v;
      }
    }

    if (yMin === Infinity) yMin = 0;
    if (yMax === -Infinity) yMax = 1;

    return { timestamps, values, validCount, yMin, yMax };
  }, [data, resolvedValueKey]);
}
